import { useCallback, useSyncExternalStore } from "react";

const listeners = new Map<string, Set<() => void>>();
const snapshotCache = new Map<string, { raw: string | null; parsed: unknown }>();

function subscribeToKey(key: string, listener: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(listener);
  return () => {
    set?.delete(listener);
    if (set?.size === 0) listeners.delete(key);
  };
}

function notifyKey(key: string): void {
  listeners.get(key)?.forEach((listener) => listener());
}

function readStorage(key: string): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.error(`useLocalStorageState: failed to read key "${key}"`, error);
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.error(`useLocalStorageState: failed to write key "${key}"`, error);
  }
}

function getCachedSnapshot<T>(key: string, defaultValue: T): T {
  const raw = readStorage(key);
  const cached = snapshotCache.get(key);
  if (cached && cached.raw === raw) return cached.parsed as T;
  let parsed: T;
  if (raw === null) {
    parsed = defaultValue;
  } else {
    try {
      parsed = JSON.parse(raw) as T;
    } catch (error) {
      console.error(`useLocalStorageState: failed to parse value for key "${key}"`, error);
      parsed = defaultValue;
    }
  }
  snapshotCache.set(key, { raw, parsed });
  return parsed;
}

export function clearLocalStorageStateCache(): void {
  snapshotCache.clear();
}

export function useLocalStorageState<T>(key: string, defaultValue: T): [T, (next: T) => void] {
  const getSnapshot = useCallback((): T => getCachedSnapshot(key, defaultValue), [key, defaultValue]);
  const getServerSnapshot = useCallback((): T => defaultValue, [defaultValue]);

  const subscribe = useCallback(
    (listener: () => void) => {
      const unsubscribe = subscribeToKey(key, listener);
      const onStorage = (event: StorageEvent) => {
        if (event.key === key) listener();
      };
      if (typeof window !== "undefined") window.addEventListener("storage", onStorage);
      return () => {
        unsubscribe();
        if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
      };
    },
    [key],
  );

  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setValue = useCallback(
    (next: T) => {
      writeStorage(key, JSON.stringify(next));
      notifyKey(key);
    },
    [key],
  );

  return [value, setValue];
}
